/**
 * Comprehensive Validation Utilities for Real Estate Management System
 * Provides client-side and server-side validation for properties, clients, and contracts
 */

// ==================== GENERAL VALIDATORS ====================

export const validators = {
  // Email validation
  isValidEmail: (email) => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  },

  // Phone validation (supports multiple formats)
  isValidPhone: (phone) => {
    if (!phone) return false;
    // Remove spaces, dashes, parentheses
    const cleanPhone = phone?.replace(/[\s\-()]/g, '');
    // Check if it's a valid phone number (8-15 digits, may start with +)
    const phoneRegex = /^\+?[0-9]{8,15}$/;
    return phoneRegex?.test(cleanPhone);
  },

  // Number validation
  isValidNumber: (value, min = null, max = null) => {
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    if (min !== null && num < min) return false;
    if (max !== null && num > max) return false;
    return true;
  },

  // Positive number validation
  isPositiveNumber: (value) => {
    const num = parseFloat(value);
    return !isNaN(num) && num > 0;
  },

  // String length validation
  isValidLength: (str, min = 0, max = Infinity) => {
    if (!str) return min === 0;
    const length = str?.trim()?.length;
    return length >= min && length <= max;
  },

  // URL validation
  isValidUrl: (url) => {
    if (!url) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },

  // Date validation
  isValidDate: (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  },

  // Future date validation
  isFutureDate: (dateString) => {
    if (!validators?.isValidDate(dateString)) return false;
    const date = new Date(dateString);
    const now = new Date();
    return date > now;
  },

  // Percentage validation (0-100)
  isValidPercentage: (value) => {
    return validators?.isValidNumber(value, 0, 100);
  },

  // Coordinate validation
  isValidLatitude: (lat) => {
    return validators?.isValidNumber(lat, -90, 90);
  },

  isValidLongitude: (lng) => {
    return validators?.isValidNumber(lng, -180, 180);
  }
};

// ==================== PROPERTY VALIDATION ====================

export const propertyValidation = {
  // Validate property data
  validate: (property) => {
    const errors = {};

    // Required fields
    if (!property?.title || !validators?.isValidLength(property?.title, 3, 200)) {
      errors.title = 'عنوان العقار مطلوب ويجب أن يكون بين 3 و 200 حرف';
    }

    if (!property?.propertyType) {
      errors.propertyType = 'نوع العقار مطلوب';
    }

    if (!property?.purpose) {
      errors.purpose = 'الغرض من العقار مطلوب (بيع/إيجار)';
    }

    // Numeric validations
    if (property?.bedrooms !== undefined && property?.bedrooms !== '' && !validators?.isValidNumber(property?.bedrooms, 0, 50)) {
      errors.bedrooms = 'عدد غرف النوم يجب أن يكون بين 0 و 50';
    }

    if (property?.bathrooms !== undefined && property?.bathrooms !== '' && !validators?.isValidNumber(property?.bathrooms, 0, 20)) {
      errors.bathrooms = 'عدد الحمامات يجب أن يكون بين 0 و 20';
    }

    if (property?.area !== undefined && property?.area !== '' && !validators?.isPositiveNumber(property?.area)) {
      errors.area = 'المساحة يجب أن تكون رقماً موجباً';
    }

    if (property?.floor !== undefined && property?.floor !== '' && !validators?.isValidNumber(property?.floor, 0, 200)) {
      errors.floor = 'رقم الطابق يجب أن يكون بين 0 و 200';
    }

    if (property?.yearBuilt !== undefined && property?.yearBuilt !== '') {
      const currentYear = new Date()?.getFullYear();
      if (!validators?.isValidNumber(property?.yearBuilt, 1800, currentYear + 5)) {
        errors.yearBuilt = `سنة البناء يجب أن تكون بين 1800 و ${currentYear + 5}`;
      }
    }

    // Location validations
    if (property?.address && !validators?.isValidLength(property?.address, 5, 500)) {
      errors.address = 'العنوان يجب أن يكون بين 5 و 500 حرف';
    }

    if (property?.city && !validators?.isValidLength(property?.city, 2, 100)) {
      errors.city = 'المدينة يجب أن تكون بين 2 و 100 حرف';
    }

    if (property?.latitude !== undefined && property?.latitude !== '' && !validators?.isValidLatitude(property?.latitude)) {
      errors.latitude = 'خط العرض يجب أن يكون بين -90 و 90';
    }

    if (property?.longitude !== undefined && property?.longitude !== '' && !validators?.isValidLongitude(property?.longitude)) {
      errors.longitude = 'خط الطول يجب أن يكون بين -180 و 180';
    }

    // Pricing validations
    if (property?.price !== undefined && property?.price !== '' && !validators?.isPositiveNumber(property?.price)) {
      errors.price = 'السعر يجب أن يكون رقماً موجباً';
    }

    if (property?.askingPrice !== undefined && property?.askingPrice !== '' && !validators?.isPositiveNumber(property?.askingPrice)) {
      errors.askingPrice = 'السعر المطلوب يجب أن يكون رقماً موجباً';
    }

    if (property?.salePrice !== undefined && property?.salePrice !== '' && !validators?.isPositiveNumber(property?.salePrice)) {
      errors.salePrice = 'سعر البيع يجب أن يكون رقماً موجباً';
    }

    if (property?.commission !== undefined && property?.commission !== '' && !validators?.isValidPercentage(property?.commission)) {
      errors.commission = 'العمولة يجب أن تكون بين 0 و 100';
    }

    if (property?.clientRetainerPercentage !== undefined && property?.clientRetainerPercentage !== '' && !validators?.isValidPercentage(property?.clientRetainerPercentage)) {
      errors.clientRetainerPercentage = 'نسبة عربون العميل يجب أن تكون بين 0 و 100';
    }

    if (property?.ownerRetainerPercentage !== undefined && property?.ownerRetainerPercentage !== '' && !validators?.isValidPercentage(property?.ownerRetainerPercentage)) {
      errors.ownerRetainerPercentage = 'نسبة عربون المالك يجب أن تكون بين 0 و 100';
    }

    // Owner validations
    if (property?.ownerEmail && !validators?.isValidEmail(property?.ownerEmail)) {
      errors.ownerEmail = 'البريد الإلكتروني للمالك غير صحيح';
    }

    if (property?.ownerPhone && !validators?.isValidPhone(property?.ownerPhone)) {
      errors.ownerPhone = 'رقم هاتف المالك غير صحيح';
    }

    // Description validation
    if (property?.description && property?.description?.length > 5000) {
      errors.description = 'الوصف يجب ألا يتجاوز 5000 حرف';
    }

    // Images validation
    if (property?.images && Array.isArray(property?.images)) {
      property?.images?.forEach((img, index) => {
        if (typeof img === 'string' && !validators?.isValidUrl(img)) {
          errors[`image_${index}`] = `رابط الصورة ${index + 1} غير صحيح`;
        }
      });
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  },

  // Validate required fields only (for quick checks)
  validateRequired: (property) => {
    const errors = {};

    if (!property?.title) {
      errors.title = 'عنوان العقار مطلوب';
    }

    if (!property?.propertyType) {
      errors.propertyType = 'نوع العقار مطلوب';
    }

    if (!property?.purpose) {
      errors.purpose = 'الغرض من العقار مطلوب';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  }
};

// ==================== CLIENT VALIDATION ====================

export const clientValidation = {
  // Validate client data
  validate: (client) => {
    const errors = {};

    // Required fields
    if (!client?.name || !validators?.isValidLength(client?.name, 2, 200)) {
      errors.name = 'اسم العميل مطلوب ويجب أن يكون بين 2 و 200 حرف';
    }

    // At least one contact method required
    if (!client?.email && !client?.phone) {
      errors.contact = 'يجب إدخال البريد الإلكتروني أو رقم الهاتف على الأقل';
    }

    // Email validation (if provided)
    if (client?.email && !validators?.isValidEmail(client?.email)) {
      errors.email = 'البريد الإلكتروني غير صحيح';
    }

    // Phone validation (if provided)
    if (client?.phone && !validators?.isValidPhone(client?.phone)) {
      errors.phone = 'رقم الهاتف غير صحيح';
    }

    // Category validation
    if (!client?.category) {
      errors.category = 'فئة العميل مطلوبة (مشتري/مستأجر/مستثمر/VIP)';
    }

    // Budget validations
    if (client?.budgetMin !== undefined && client?.budgetMin !== '' && !validators?.isPositiveNumber(client?.budgetMin)) {
      errors.budgetMin = 'الحد الأدنى للميزانية يجب أن يكون رقماً موجباً';
    }

    if (client?.budgetMax !== undefined && client?.budgetMax !== '' && !validators?.isPositiveNumber(client?.budgetMax)) {
      errors.budgetMax = 'الحد الأقصى للميزانية يجب أن يكون رقماً موجباً';
    }

    // Budget range validation
    if (client?.budgetMin && client?.budgetMax) {
      const min = parseFloat(client?.budgetMin);
      const max = parseFloat(client?.budgetMax);
      if (min > max) {
        errors.budgetRange = 'الحد الأدنى للميزانية يجب أن يكون أقل من الحد الأقصى';
      }
    }

    // Bedrooms validation
    if (client?.bedrooms !== undefined && client?.bedrooms !== '' && !validators?.isValidNumber(client?.bedrooms, 0, 50)) {
      errors.bedrooms = 'عدد غرف النوم يجب أن يكون بين 0 و 50';
    }

    // Notes validation
    if (client?.notes && client?.notes?.length > 2000) {
      errors.notes = 'الملاحظات يجب ألا تتجاوز 2000 حرف';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  },

  // Validate required fields only
  validateRequired: (client) => {
    const errors = {};

    if (!client?.name) {
      errors.name = 'اسم العميل مطلوب';
    }

    if (!client?.email && !client?.phone) {
      errors.contact = 'يجب إدخال البريد الإلكتروني أو رقم الهاتف على الأقل';
    }

    if (!client?.category) {
      errors.category = 'فئة العميل مطلوبة';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  }
};

// ==================== CONTRACT VALIDATION ====================

export const contractValidation = {
  // Validate base contract data
  validate: (contract) => {
    const errors = {};

    // Required fields
    if (!contract?.contractNumber || !validators?.isValidLength(contract?.contractNumber, 3, 50)) {
      errors.contractNumber = 'رقم العقد مطلوب ويجب أن يكون بين 3 و 50 حرف';
    }

    if (!contract?.contractType) {
      errors.contractType = 'نوع العقد مطلوب';
    }

    if (!contract?.propertyId && !contract?.clientId) {
      errors.references = 'يجب ربط العقد بعقار أو عميل على الأقل';
    }

    // Date validations
    if (contract?.startDate && !validators?.isValidDate(contract?.startDate)) {
      errors.startDate = 'تاريخ البداية غير صحيح';
    }

    if (contract?.endDate && !validators?.isValidDate(contract?.endDate)) {
      errors.endDate = 'تاريخ النهاية غير صحيح';
    }

    // Date range validation
    if (contract?.startDate && contract?.endDate) {
      const start = new Date(contract.startDate);
      const end = new Date(contract.endDate);
      if (start >= end) {
        errors.dateRange = 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية';
      }
    }

    // Amount validation
    if (contract?.amount !== undefined && contract?.amount !== '' && !validators?.isPositiveNumber(contract?.amount)) {
      errors.amount = 'قيمة العقد يجب أن تكون رقماً موجباً';
    }

    // Terms validation
    if (contract?.terms && contract?.terms?.length > 10000) {
      errors.terms = 'شروط العقد يجب ألا تتجاوز 10000 حرف';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  },

  // Validate marketing contract
  validateMarketingContract: (contract) => {
    const errors = {};

    // Base validation
    const baseValidation = contractValidation?.validate(contract);
    Object.assign(errors, baseValidation?.errors);

    // Marketing-specific validations
    if (contract?.arddjoRetainerPercentage !== undefined && !validators?.isValidPercentage(contract?.arddjoRetainerPercentage)) {
      errors.arddjoRetainerPercentage = 'نسبة عربون أردجو يجب أن تكون بين 0 و 100';
    }

    if (contract?.ownerRetainerPercentage !== undefined && !validators?.isValidPercentage(contract?.ownerRetainerPercentage)) {
      errors.ownerRetainerPercentage = 'نسبة عربون المالك يجب أن تكون بين 0 و 100';
    }

    if (contract?.marketingDurationMonths !== undefined && !validators?.isValidNumber(contract?.marketingDurationMonths, 1, 120)) {
      errors.marketingDurationMonths = 'مدة التسويق يجب أن تكون بين 1 و 120 شهر';
    }

    if (contract?.targetPrice !== undefined && !validators?.isPositiveNumber(contract?.targetPrice)) {
      errors.targetPrice = 'السعر المستهدف يجب أن يكون رقماً موجباً';
    }

    if (contract?.minimumPrice !== undefined && !validators?.isPositiveNumber(contract?.minimumPrice)) {
      errors.minimumPrice = 'الحد الأدنى للسعر يجب أن يكون رقماً موجباً';
    }

    // Price range validation
    if (contract?.targetPrice && contract?.minimumPrice) {
      const target = parseFloat(contract?.targetPrice);
      const minimum = parseFloat(contract?.minimumPrice);
      if (minimum > target) {
        errors.priceRange = 'الحد الأدنى للسعر يجب أن يكون أقل من السعر المستهدف';
      }
    }

    if (!contract?.marketingChannels || contract?.marketingChannels?.length === 0) {
      errors.marketingChannels = 'يجب اختيار قناة تسويقية واحدة على الأقل';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  },

  // Validate buyer representation contract
  validateBuyerContract: (contract) => {
    const errors = {};

    // Base validation
    const baseValidation = contractValidation?.validate(contract);
    Object.assign(errors, baseValidation?.errors);

    // Buyer contract-specific validations
    if (contract?.arddjoRetainerPercentage !== undefined && !validators?.isValidPercentage(contract?.arddjoRetainerPercentage)) {
      errors.arddjoRetainerPercentage = 'نسبة عربون أردجو يجب أن تكون بين 0 و 100';
    }

    if (contract?.clientRetainerPercentage !== undefined && !validators?.isValidPercentage(contract?.clientRetainerPercentage)) {
      errors.clientRetainerPercentage = 'نسبة عربون العميل يجب أن تكون بين 0 و 100';
    }

    if (contract?.representationDurationMonths !== undefined && !validators?.isValidNumber(contract?.representationDurationMonths, 1, 120)) {
      errors.representationDurationMonths = 'مدة التمثيل يجب أن تكون بين 1 و 120 شهر';
    }

    if (contract?.budgetMin !== undefined && !validators?.isPositiveNumber(contract?.budgetMin)) {
      errors.budgetMin = 'الحد الأدنى للميزانية يجب أن يكون رقماً موجباً';
    }

    if (contract?.budgetMax !== undefined && !validators?.isPositiveNumber(contract?.budgetMax)) {
      errors.budgetMax = 'الحد الأقصى للميزانية يجب أن يكون رقماً موجباً';
    }

    // Budget range validation
    if (contract?.budgetMin && contract?.budgetMax) {
      const min = parseFloat(contract?.budgetMin);
      const max = parseFloat(contract?.budgetMax);
      if (min > max) {
        errors.budgetRange = 'الحد الأدنى للميزانية يجب أن يكون أقل من الحد الأقصى';
      }
    }

    if (!contract?.propertyTypes || contract?.propertyTypes?.length === 0) {
      errors.propertyTypes = 'يجب اختيار نوع عقار واحد على الأقل';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  }
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Sanitize input to prevent XSS attacks
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input?.replace(/</g, '&lt;')?.replace(/>/g, '&gt;')?.replace(/"/g, '&quot;')?.replace(/'/g, '&#x27;')?.replace(/\//g, '&#x2F;');
};

/**
 * Format validation errors for display
 */
export const formatValidationErrors = (errors) => {
  return Object.entries(errors)?.map(([field, message]) => message)?.join('\n');
};

/**
 * Check if form has any errors
 */
export const hasErrors = (errors) => {
  return Object.keys(errors)?.length > 0;
};

// ==================== OWNER VALIDATION ====================

export const ownerValidation = {
  // Validate owner data
  validate: (owner) => {
    const errors = {};

    // Required fields
    if (!owner?.name || !validators?.isValidLength(owner?.name, 2, 200)) {
      errors.name = 'اسم المالك مطلوب ويجب أن يكون بين 2 و 200 حرف';
    }

    // Phone validation (required)
    if (!owner?.phone) {
      errors.phone = 'رقم الهاتف مطلوب';
    } else if (!validators?.isValidPhone(owner?.phone)) {
      errors.phone = 'رقم الهاتف غير صحيح';
    }

    // Email validation (optional but must be valid if provided)
    if (owner?.email && !validators?.isValidEmail(owner?.email)) {
      errors.email = 'البريد الإلكتروني غير صحيح';
    }

    // National ID validation (optional but must be valid if provided)
    if (owner?.nationalId && !validators?.isValidLength(owner?.nationalId, 5, 50)) {
      errors.nationalId = 'رقم الهوية يجب أن يكون بين 5 و 50 حرف';
    }

    // Bank account validation (optional)
    if (owner?.bankAccount && !validators?.isValidLength(owner?.bankAccount, 5, 100)) {
      errors.bankAccount = 'رقم الحساب البنكي يجب أن يكون بين 5 و 100 حرف';
    }

    // Address validation (optional)
    if (owner?.address && !validators?.isValidLength(owner?.address, 5, 500)) {
      errors.address = 'العنوان يجب أن يكون بين 5 و 500 حرف';
    }

    // Notes validation
    if (owner?.notes && owner?.notes?.length > 2000) {
      errors.notes = 'الملاحظات يجب ألا تتجاوز 2000 حرف';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  },

  // Validate required fields only
  validateRequired: (owner) => {
    const errors = {};

    if (!owner?.name) {
      errors.name = 'اسم المالك مطلوب';
    }

    if (!owner?.phone) {
      errors.phone = 'رقم الهاتف مطلوب';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  }
};

// ==================== USER VALIDATION ====================

export const userValidation = {
  // Validate user profile data
  validate: (user) => {
    const errors = {};

    // Required fields
    if (!user?.fullName || !validators?.isValidLength(user?.fullName, 2, 200)) {
      errors.fullName = 'الاسم الكامل مطلوب ويجب أن يكون بين 2 و 200 حرف';
    }

    // Email validation (required and must be unique)
    if (!user?.email) {
      errors.email = 'البريد الإلكتروني مطلوب';
    } else if (!validators?.isValidEmail(user?.email)) {
      errors.email = 'البريد الإلكتروني غير صحيح';
    }

    // Phone validation (optional but must be valid if provided)
    if (user?.phone && !validators?.isValidPhone(user?.phone)) {
      errors.phone = 'رقم الهاتف غير صحيح';
    }

    // Role validation (required, must be from enum)
    const validRoles = ['admin', 'manager', 'agent', 'staff'];
    if (!user?.role) {
      errors.role = 'الدور الوظيفي مطلوب';
    } else if (!validRoles?.includes(user?.role)) {
      errors.role = 'الدور الوظيفي غير صحيح';
    }

    // Avatar URL validation (optional)
    if (user?.avatarUrl && !validators?.isValidUrl(user?.avatarUrl)) {
      errors.avatarUrl = 'رابط الصورة الشخصية غير صحيح';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  },

  // Validate required fields only
  validateRequired: (user) => {
    const errors = {};

    if (!user?.fullName) {
      errors.fullName = 'الاسم الكامل مطلوب';
    }

    if (!user?.email) {
      errors.email = 'البريد الإلكتروني مطلوب';
    }

    if (!user?.role) {
      errors.role = 'الدور الوظيفي مطلوب';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  }
};

// ==================== APPOINTMENT VALIDATION ====================

export const appointmentValidation = {
  // Validate appointment data
  validate: (appointment) => {
    const errors = {};

    // Required fields
    if (!appointment?.title || !validators?.isValidLength(appointment?.title, 3, 200)) {
      errors.title = 'عنوان الموعد مطلوب ويجب أن يكون بين 3 و 200 حرف';
    }

    // Date validation (required and must be valid)
    if (!appointment?.appointmentDate) {
      errors.appointmentDate = 'تاريخ الموعد مطلوب';
    } else if (!validators?.isValidDate(appointment?.appointmentDate)) {
      errors.appointmentDate = 'تاريخ الموعد غير صحيح';
    }

    // Duration validation (optional, default 60 minutes)
    if (appointment?.duration !== undefined && appointment?.duration !== '' && !validators?.isValidNumber(appointment?.duration, 15, 480)) {
      errors.duration = 'مدة الموعد يجب أن تكون بين 15 و 480 دقيقة';
    }

    // Location validation (optional)
    if (appointment?.location && !validators?.isValidLength(appointment?.location, 3, 500)) {
      errors.location = 'موقع الموعد يجب أن يكون بين 3 و 500 حرف';
    }

    // Description validation (optional)
    if (appointment?.description && appointment?.description?.length > 2000) {
      errors.description = 'الوصف يجب ألا يتجاوز 2000 حرف';
    }

    // Status validation (must be from enum)
    const validStatuses = ['scheduled', 'completed', 'cancelled', 'rescheduled'];
    if (appointment?.status && !validStatuses?.includes(appointment?.status)) {
      errors.status = 'حالة الموعد غير صحيحة';
    }

    // At least one reference required (property or client)
    if (!appointment?.propertyId && !appointment?.clientId) {
      errors.references = 'يجب ربط الموعد بعقار أو عميل على الأقل';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  },

  // Validate required fields only
  validateRequired: (appointment) => {
    const errors = {};

    if (!appointment?.title) {
      errors.title = 'عنوان الموعد مطلوب';
    }

    if (!appointment?.appointmentDate) {
      errors.appointmentDate = 'تاريخ الموعد مطلوب';
    }

    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  }
};

// ==================== SUPABASE SCHEMA VALIDATORS ====================

/**
 * Validate against Supabase schema constraints
 * These validators check data types, enums, and constraints before saving to database
 */
export const supabaseSchemaValidation = {
  // Property type enum validation
  validatePropertyType: (type) => {
    const validTypes = ['شقة', 'فيلا', 'أرض', 'مكتب', 'محل تجاري', 'مستودع', 'مزرعة'];
    return validTypes?.includes(type);
  },

  // Property purpose enum validation
  validatePropertyPurpose: (purpose) => {
    const validPurposes = ['بيع', 'إيجار', 'بيع وإيجار'];
    return validPurposes?.includes(purpose);
  },

  // Property status enum validation
  validatePropertyStatus: (status) => {
    const validStatuses = ['متاح', 'محجوز', 'مباع', 'مؤجر', 'معلق', 'منتهي'];
    return validStatuses?.includes(status);
  },

  // Furnishing type enum validation
  validateFurnishingType: (furnishing) => {
    const validTypes = ['مفروش', 'نصف مفروش', 'غير مفروش'];
    return validTypes?.includes(furnishing);
  },

  // Client category enum validation
  validateClientCategory: (category) => {
    const validCategories = ['مشتري', 'مستأجر', 'مستثمر', 'VIP'];
    return validCategories?.includes(category);
  },

  // Client status enum validation
  validateClientStatus: (status) => {
    const validStatuses = ['active', 'pending', 'inactive'];
    return validStatuses?.includes(status);
  },

  // Priority level enum validation
  validatePriorityLevel: (priority) => {
    const validPriorities = ['high', 'medium', 'low'];
    return validPriorities?.includes(priority);
  },

  // User role enum validation
  validateUserRole: (role) => {
    const validRoles = ['admin', 'manager', 'agent', 'staff'];
    return validRoles?.includes(role);
  },

  // Contract status enum validation
  validateContractStatus: (status) => {
    const validStatuses = ['pending', 'approved', 'rejected', 'active', 'expired'];
    return validStatuses?.includes(status);
  },

  // Appointment status enum validation
  validateAppointmentStatus: (status) => {
    const validStatuses = ['scheduled', 'completed', 'cancelled', 'rescheduled'];
    return validStatuses?.includes(status);
  },

  // Validate UUID format
  validateUUID: (uuid) => {
    if (!uuid) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex?.test(uuid);
  },

  // Validate numeric constraints
  validateNumericConstraint: (value, min, max) => {
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    if (min !== null && num < min) return false;
    if (max !== null && num > max) return false;
    return true;
  },

  // Validate text length constraints
  validateTextLength: (text, maxLength) => {
    if (!text) return true;
    return text?.length <= maxLength;
  },

  // Validate array constraints
  validateArray: (arr, minLength = 0, maxLength = Infinity) => {
    if (!Array.isArray(arr)) return false;
    return arr?.length >= minLength && arr?.length <= maxLength;
  }
};

// ==================== REAL-TIME VALIDATION ====================

/**
 * Real-time field validation for immediate user feedback
 * Returns error message or null if valid
 */
export const realtimeValidation = {
  // Validate field on change
  validateField: (fieldName, value, formData = {}) => {
    switch (fieldName) {
      // Email fields
      case 'email': case'ownerEmail': case'clientEmail':
        if (value && !validators?.isValidEmail(value)) {
          return 'البريد الإلكتروني غير صحيح';
        }
        break;

      // Phone fields
      case 'phone': case'ownerPhone': case'clientPhone':
        if (value && !validators?.isValidPhone(value)) {
          return 'رقم الهاتف غير صحيح';
        }
        break;

      // Numeric fields
      case 'price': case'askingPrice': case'salePrice': case'amount': case'targetPrice': case'minimumPrice':
        if (value && !validators?.isPositiveNumber(value)) {
          return 'يجب إدخال رقم موجب';
        }
        break;

      // Percentage fields
      case 'commission': case'arddjoRetainerPercentage': case'ownerRetainerPercentage': case'clientRetainerPercentage':
        if (value && !validators?.isValidPercentage(value)) {
          return 'النسبة يجب أن تكون بين 0 و 100';
        }
        break;

      // Budget range validation
      case 'budgetMin':
        if (value && formData?.budgetMax) {
          const min = parseFloat(value);
          const max = parseFloat(formData?.budgetMax);
          if (!isNaN(min) && !isNaN(max) && min > max) {
            return 'الحد الأدنى يجب أن يكون أقل من الحد الأقصى';
          }
        }
        break;

      case 'budgetMax':
        if (value && formData?.budgetMin) {
          const min = parseFloat(formData?.budgetMin);
          const max = parseFloat(value);
          if (!isNaN(min) && !isNaN(max) && max < min) {
            return 'الحد الأقصى يجب أن يكون أكبر من الحد الأدنى';
          }
        }
        break;

      // Date range validation
      case 'startDate':
        if (value && formData?.endDate) {
          const start = new Date(value);
          const end = new Date(formData?.endDate);
          if (start >= end) {
            return 'تاريخ البداية يجب أن يكون قبل تاريخ النهاية';
          }
        }
        break;

      case 'endDate':
        if (value && formData?.startDate) {
          const start = new Date(formData?.startDate);
          const end = new Date(value);
          if (end <= start) {
            return 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية';
          }
        }
        break;

      // URL fields
      case 'avatarUrl': case'logoUrl':
        if (value && !validators?.isValidUrl(value)) {
          return 'الرابط غير صحيح';
        }
        break;

      default:
        return null;
    }

    return null;
  },

  // Validate on blur (when user leaves field)
  validateOnBlur: (fieldName, value, isRequired = false) => {
    // Check if required field is empty
    if (isRequired && (!value || (typeof value === 'string' && !value?.trim()))) {
      return 'هذا الحقل مطلوب';
    }

    // Run field-specific validation
    return realtimeValidation?.validateField(fieldName, value);
  }
};

// ==================== EXPORT ALL ====================

export default {
  validators,
  propertyValidation,
  clientValidation,
  contractValidation,
  ownerValidation,
  userValidation,
  appointmentValidation,
  supabaseSchemaValidation,
  realtimeValidation,
  sanitizeInput,
  formatValidationErrors,
  hasErrors
};